import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CheckCheck, User, Phone as PhoneIcon, Mail } from "lucide-react";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import { formatPhone } from "../lib/formatters";
import { CHAT_OPEN_EVENT } from "../lib/chatBus";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "locrnc_chat_session_v1";
const POLL_MS = 3000;

const COPY = {
  fr: {
    header: "Phil · Location RNC",
    status: "En ligne · Phil répond très vite",
    intro1: "Bonjour ! 👋",
    intro2: "Ici l'équipe de Location RNC. Laissez-nous vos coordonnées et posez votre question — Phil vous répond directement dans le chat.",
    startTitle: "Démarrer la conversation",
    nameLbl: "Votre nom",
    phoneLbl: "Téléphone",
    emailLbl: "Courriel (optionnel)",
    startCta: "Ouvrir le chat",
    placeholder: "Écrivez votre message…",
    sendAria: "Envoyer",
    typing: "Phil est en train d'écrire…",
    savedFooter: "Cette conversation reste dans votre navigateur.",
    reset: "Nouvelle conversation",
    errorSend: "Impossible d'envoyer. Réessayez.",
  },
  en: {
    header: "Phil · Location RNC",
    status: "Online · Phil replies fast",
    intro1: "Hi there! 👋",
    intro2: "You've reached Location RNC. Drop your info and question — Phil answers you right in the chat.",
    startTitle: "Start the chat",
    nameLbl: "Your name",
    phoneLbl: "Phone",
    emailLbl: "Email (optional)",
    startCta: "Open the chat",
    placeholder: "Type your message…",
    sendAria: "Send",
    typing: "Phil is typing…",
    savedFooter: "This chat stays saved on your device.",
    reset: "New chat",
    errorSend: "Send failed. Try again.",
  },
};

const timeStamp = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function LiveChat() {
  const { pathname } = useLocation();
  const [heroActionsVisible, setHeroActionsVisible] = useState(false);
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.fr;

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [session, setSession] = useState(null); // {session_id, name}
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [unread, setUnread] = useState(0);
  const feedRef = useRef(null);
  const inputRef = useRef(null);
  const lastSeenRef = useRef(null); // ISO timestamp

  // Avoid covering the mobile hero actions with the floating chat control.
  useEffect(() => {
    setHeroActionsVisible(false);
    const media = window.matchMedia('(max-width: 639px)');
    let inView = false;
    const update = () => setHeroActionsVisible(media.matches && inView);
    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting; update();
    });
    const watchActions = () => {
      const actions = document.querySelector('.hero-actions');
      if (actions) { observer.observe(actions); mutations.disconnect(); }
    };
    const mutations = new MutationObserver(watchActions);
    if (pathname === '/') {
      mutations.observe(document.getElementById('root'), { childList: true, subtree: true });
      watchActions();
    }
    media.addEventListener('change', update);
    return () => { observer.disconnect(); mutations.disconnect(); media.removeEventListener('change', update); };
  }, [pathname]);

  // hydrate session from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s?.session_id) setSession(s);
      }
    } catch {}
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // Listen for global openChatWith(text) events (from any page's "Réserver" button)
  useEffect(() => {
    const handler = (e) => {
      const text = e?.detail?.text || "";
      setOpen(true);
      if (text) {
        setDraft(text);
        setTimeout(() => inputRef.current?.focus(), 250);
      }
    };
    window.addEventListener(CHAT_OPEN_EVENT, handler);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, handler);
  }, []);

  // fetch messages helper
  const fetchMessages = useCallback(async () => {
    if (!session?.session_id) return;
    try {
      const params = { session_id: session.session_id };
      if (lastSeenRef.current) params.since = lastSeenRef.current;
      const { data } = await axios.get(`${API}/chat/messages`, { params });
      if (data.length) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const merged = [...prev];
          for (const m of data) if (!ids.has(m.id)) merged.push(m);
          return merged;
        });
        lastSeenRef.current = data[data.length - 1].created_at;
        // increment unread count for admin messages when chat closed
        const newAdmin = data.filter((m) => m.author === "admin").length;
        if (newAdmin && !open) setUnread((u) => u + newAdmin);
      }
    } catch {}
  }, [session, open]);

  // initial + polling
  useEffect(() => {
    if (!session?.session_id) return;
    fetchMessages();
    const id = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(id);
  }, [session, fetchMessages]);

  // clear unread when opening
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // auto-scroll
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages, open, session]);

  const startChat = async (e) => {
    e?.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Nom et téléphone requis.");
      return;
    }
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/chat/sessions`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
      });
      const s = { session_id: data.session_id, name: data.name };
      setSession(s);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
      // seed intro bubbles locally so it doesn't hit the DB
      setMessages([
        { id: "intro-1", author: "admin", text: c.intro1, created_at: new Date().toISOString(), local: true },
        { id: "intro-2", author: "admin", text: c.intro2, created_at: new Date().toISOString(), local: true },
      ]);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      setError(c.errorSend);
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !session?.session_id) return;
    setSending(true);
    // optimistic
    const temp = { id: `tmp-${Date.now()}`, author: "visitor", text, created_at: new Date().toISOString(), pending: true };
    setMessages((m) => [...m, temp]);
    setDraft("");
    try {
      const { data } = await axios.post(
        `${API}/chat/messages`,
        { text },
        { params: { session_id: session.session_id } }
      );
      setMessages((m) => m.map((x) => (x.id === temp.id ? { ...data, delivered: true } : x)));
      lastSeenRef.current = data.created_at;
    } catch {
      setMessages((m) => m.map((x) => (x.id === temp.id ? { ...x, error: true, pending: false } : x)));
      setError(c.errorSend);
    } finally {
      setSending(false);
    }
  };

  const resetSession = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setMessages([]);
    setForm({ name: "", phone: "", email: "" });
    lastSeenRef.current = null;
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* PANEL */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed bottom-24 right-4 md:right-8 z-[60] w-[calc(100vw-2rem)] max-w-[380px] bg-white border border-[#E2E8F0] shadow-[0_25px_60px_-15px_rgba(15,23,42,0.35)] rounded-md overflow-hidden flex flex-col"
            style={{ maxHeight: "min(78vh, 660px)" }}
            data-testid="chat-panel"
          >
            {/* HEADER */}
            <div className="relative bg-gradient-to-br from-[#128C7E] to-[#25D366] px-4 py-3 text-white shrink-0">
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="chat-close"
                className="absolute top-2.5 right-2.5 p-1 text-white/85 hover:text-white transition-colors rounded-full hover:bg-white/15"
              >
                <X size={16} />
              </button>
              <div className="flex items-center gap-3 pr-8">
                <div className="relative w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur">
                  <MessageCircle size={18} className="text-white" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#B9F84A] border-2 border-[#128C7E]" />
                </div>
                <div className="leading-tight">
                  <div className="font-display text-base uppercase tracking-wide">{c.header}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-body uppercase tracking-[0.18em] text-white/85">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B9F84A] animate-pulse" />
                    {c.status}
                  </div>
                </div>
              </div>
            </div>

            {/* BODY: FORM or FEED */}
            {!session ? (
              <form onSubmit={startChat} className="p-5 bg-[#F5F1EA]" data-testid="chat-start-form">
                <div className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-[#128C7E]">
                  {c.intro1}
                </div>
                <div className="font-display text-2xl uppercase text-[#0F172A] mt-2 leading-tight">
                  {c.startTitle}
                </div>
                <p className="mt-2 font-body text-sm text-[#475569] leading-relaxed">{c.intro2}</p>

                {draft && (
                  <div className="mt-4 p-3 bg-white border-l-4 border-[#E87B3A] rounded-sm" data-testid="chat-pending-preview">
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.22em] text-[#E87B3A]">
                      Message à envoyer
                    </div>
                    <div className="mt-1.5 font-body text-[13px] text-[#0F172A] leading-relaxed whitespace-pre-wrap break-words">
                      {draft}
                    </div>
                  </div>
                )}

                <div className="mt-5 space-y-3">
                  <Field icon={<User size={14} />} label={c.nameLbl}>
                    <input
                      type="text" required autoFocus
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="chat-input-name"
                      className="chat-input"
                    />
                  </Field>
                  <Field icon={<PhoneIcon size={14} />} label={c.phoneLbl}>
                    <input
                      type="tel" required
                      placeholder="555-555-5555"
                      maxLength={12}
                      inputMode="numeric"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                      data-testid="chat-input-phone"
                      className="chat-input"
                    />
                  </Field>
                  <Field icon={<Mail size={14} />} label={c.emailLbl}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="chat-input-email"
                      className="chat-input"
                    />
                  </Field>
                </div>

                {error && (
                  <div className="mt-3 text-red-600 text-xs font-body">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={starting}
                  data-testid="chat-start-btn"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5B] disabled:opacity-60 text-white px-4 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  <MessageCircle size={14} /> {starting ? "…" : c.startCta}
                </button>

                <style>{`
                  .chat-input {
                    display:block; width:100%;
                    padding:10px 12px;
                    background:#fff;
                    border:1px solid #E2E8F0;
                    border-radius:6px;
                    font-family:'Manrope', sans-serif;
                    font-size:13.5px;
                    color:#0F172A;
                    outline:none;
                    transition:border-color .2s ease;
                  }
                  .chat-input:focus{ border-color:#128C7E; box-shadow:0 0 0 3px rgba(18,140,126,0.12); }
                `}</style>
              </form>
            ) : (
              <>
                {/* FEED */}
                <div
                  ref={feedRef}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-[#E7DFD3]"
                  data-testid="chat-feed"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M20 5v6M8 12l4 4M32 12l-4 4M5 22h6M35 22h-6M20 35v-6M8 30l4-4M32 30l-4-4' stroke='rgba(15,23,42,0.05)' stroke-width='1' fill='none'/></svg>\")",
                  }}
                >
                  <AnimatePresence initial={false}>
                    {messages.map((m) => (
                      <motion.div
                        key={m.id}
                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.22 }}
                        className={`flex ${m.author === "visitor" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] px-3 py-2 rounded-lg shadow-[0_1px_1px_rgba(15,23,42,0.08)] ${
                            m.author === "visitor"
                              ? "bg-[#DCF8C6] rounded-tr-sm"
                              : "bg-white rounded-tl-sm"
                          } ${m.error ? "opacity-70" : ""}`}
                        >
                          <div className="font-body text-[13.5px] text-[#0F172A] leading-relaxed whitespace-pre-wrap break-words">
                            {m.text}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 justify-end text-[10px] text-[#475569]">
                            <span>{timeStamp(m.created_at)}</span>
                            {m.author === "visitor" && !m.local && (
                              m.error
                                ? <span className="text-red-500">!</span>
                                : m.pending
                                  ? <span className="opacity-60">·</span>
                                  : <CheckCheck size={12} className="text-[#128C7E]" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* INPUT */}
                <div className="border-t border-[#E2E8F0] bg-[#F5F1EA] px-3 py-2.5 shrink-0">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder={c.placeholder}
                      rows={1}
                      data-testid="chat-input"
                      className="flex-1 max-h-24 resize-none bg-white border border-[#E2E8F0] rounded-2xl px-3.5 py-2 font-body text-[13.5px] text-[#0F172A] focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/10"
                    />
                    <button
                      onClick={send}
                      aria-label={c.sendAria}
                      data-testid="chat-send"
                      className="w-10 h-10 shrink-0 rounded-full bg-[#25D366] hover:bg-[#1EBE5B] text-white flex items-center justify-center transition-colors disabled:opacity-50"
                      disabled={!draft.trim() || sending}
                    >
                      <Send size={16} />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[9px] font-body uppercase tracking-widest text-[#94A3B8]">
                    <span>{c.savedFooter}</span>
                    <button
                      onClick={resetSession}
                      data-testid="chat-reset"
                      className="hover:text-[#E87B3A] transition-colors"
                    >
                      {c.reset}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <AnimatePresence>
        {visible && (!heroActionsVisible || open) && (
          <motion.button
            key="chat-fab"
            initial={{ opacity: 0, scale: 0.4, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            transition={{ type: "spring", stiffness: 280, damping: 18 }}
            onClick={() => setOpen((v) => !v)}
            aria-label="Chat"
            data-testid="chat-fab"
            className="fixed bottom-5 right-4 md:right-8 z-[60] w-14 h-14 rounded-full bg-[#25D366] shadow-[0_15px_35px_-8px_rgba(37,211,102,0.65)] flex items-center justify-center text-white hover:bg-[#1EBE5B] transition-colors"
          >
            {!open && <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />}
            <span className="relative">
              {open ? <X size={22} /> : <MessageCircle size={24} />}
            </span>
            {!open && unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#E87B3A] text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
                {unread}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

const Field = ({ icon, label, children }) => (
  <label className="block">
    <div className="flex items-center gap-2 font-body text-[10px] font-bold uppercase tracking-[0.25em] text-[#475569] mb-1.5">
      <span className="text-[#128C7E]">{icon}</span>
      {label}
    </div>
    {children}
  </label>
);
